// Pure Textbildung fuer die Kollisions-Hinweise. Bewusst ohne DOM und ohne Netz, damit die
// Formulierung testbar bleibt und settings-page.ts nicht weiter waechst (die Datei ist bereits
// ueber 1700 Zeilen).

export interface ConflictRow {
  kind: string;
  vario_attribute_id: string;
  option_id_low: string;
  option_id_high: string;
}

/**
 * Ein Satz im Klartext, der die Kollision beschreibt — mit NAMEN, nicht mit IDs. Eine technische ID
 * hilft dem Anwender nicht weiter; sie erscheint nur dort, wo sie das einzige Unterscheidungsmerkmal
 * ist: wenn beide Gambio-Optionen GLEICH heissen (der reale Vorfall — „Größe" fuer S/M/L und
 * „Größe" fuer 36-46). Dann steht sie ausgeschrieben als „(Gambio-Option 3)" da, nicht nur als „(3)".
 * Ein fehlender Name wird als „unbekannt" benannt; die rohe Attribut-ID wird NIE ausgegeben, denn
 * fehlt das Label, liegt das an der nicht geladenen Zuordnung und nicht an den Daten.
 *
 * ACHTUNG: Der Rueckgabewert ist UNESCAPTER Klartext und enthaelt Shop-Daten (Options-Namen,
 * Attribut-Labels). Wer ihn in HTML einsetzt, muss selbst escapen (`escHtml`) — ein `innerHTML +=`
 * oder ein `title="…"` ohne Escaping macht daraus lautlos eine XSS-Luecke.
 */
/** Sehen zwei Namen fuer den Anwender gleich aus? Klein geschrieben, ß als ss, Rand-Leerzeichen weg. */
function sameToTheEye(a: string, b: string): boolean {
  const norm = (s: string): string => s.trim().toLowerCase().replace(/ß/g, 'ss');
  return norm(a) === norm(b);
}

export function conflictSentence(
  row: ConflictRow,
  optionNames: ReadonlyMap<string, string>,
  attributeLabels: ReadonlyMap<string, string>,
): string {
  const lowName = optionNames.get(row.option_id_low) ?? 'unbekannt';
  const highName = optionNames.get(row.option_id_high) ?? 'unbekannt';
  // Bewusst grosszuegig verglichen: „Größe"/„GRÖSSE" sind fuer das Auge dasselbe, `toLowerCase`
  // allein sieht das wegen ß/ss nicht. Eine Nummer zu viel ist harmlos, zwei fuer den Anwender
  // gleich aussehende Optionen OHNE Unterscheidungsmerkmal sind es nicht.
  const ambiguous = sameToTheEye(lowName, highName);
  const low = ambiguous ? `${lowName}" (Gambio-Option ${row.option_id_low})` : `${lowName}"`;
  const high = ambiguous ? `${highName}" (Gambio-Option ${row.option_id_high})` : `${highName}"`;
  const attr = attributeLabels.get(row.vario_attribute_id) ?? 'unbekannt';
  return `Die Gambio-Optionen „${low} und „${high} zeigen beide auf das Vario-Attribut „${attr}".`;
}
